import moment from "moment";
import "moment/locale/th";

// ---- Option builders ----

/**
 * Returns an array of 24 monthly options (current month first), using moment's
 * Thai locale for the display text, matching ClinicDashboard.vue's buildMonthOptions().
 * @returns {{ value: string, text: string }[]}  value = "YYYY-MM", text = "Thai month name + Buddhist year"
 */
export function buildMonthOptions() {
  moment.locale("th");
  const options = [];
  for (let i = 0; i < 24; i++) {
    const date = moment().subtract(i, "months");
    options.push({
      value: date.format("YYYY-MM"),
      text: date.locale("th").format("MMMM") + " " + (date.year() + 543),
    });
  }
  return options;
}

/**
 * Returns an array of 3 yearly options (current year first), with Buddhist Era
 * text, matching ClinicDashboard.vue's buildYearOptions().
 * @returns {{ value: string, text: string }[]}  value = "YYYY", text = "ปี <BE>"
 */
export function buildYearOptions() {
  const currentYear = moment().year();
  const years = [];
  for (let i = 0; i < 3; i++) {
    const year = currentYear - i;
    years.push({ value: year.toString(), text: `ปี ${year + 543}` });
  }
  return years;
}

// ---- Date range ----

/**
 * Returns { startDt, endDt } as ISO strings (moment().format() — includes local +07 offset)
 * for the given viewType and selection parameters.
 *
 * @param {"month"|"year"|"day"} viewType
 * @param {{ month?: string, year?: string, day?: string }} params
 * @returns {{ startDt: string, endDt: string }}
 */
export function rangeFor(viewType, { month, year, day } = {}) {
  if (viewType === "year") {
    return {
      startDt: moment(year).startOf("year").format(),
      endDt: moment(year).endOf("year").format(),
    };
  }
  if (viewType === "day") {
    return {
      startDt: moment(day).startOf("day").format(),
      endDt: moment(day).endOf("day").format(),
    };
  }
  // default: "month"
  return {
    startDt: moment(month).startOf("month").format(),
    endDt: moment(month).endOf("month").format(),
  };
}

// ---- Comparison labels ----

/**
 * Returns Thai Buddhist-Era comparison period labels for display in the toggle buttons.
 *
 * Buddhist short year = (gregorianYear + 543) % 100  (2-digit)
 *
 * @param {"month"|"year"|"day"} viewType
 * @param {{ month?: string, year?: string, day?: string }} params
 * @returns {{ prevPeriodLabel: string, prevYearLabel: string }}
 */
export function comparisonLabels(viewType, { month, year, day } = {}, now = moment()) {
  if (viewType === "day") {
    const prevDay = moment(day).subtract(1, "day");
    // มุมมองรายวันเทียบกับ "วันเดียวกันสัปดาห์ก่อน" (−7 วัน) เช่น อังคาร → อังคารที่แล้ว
    const lastWeek = moment(day).subtract(7, "day").locale("th");
    const prevBEshort = (prevDay.year() + 543) % 100;
    const lastWeekBEshort = (lastWeek.year() + 543) % 100;
    return {
      prevPeriodLabel:
        "เมื่อวาน " + prevDay.locale("th").format("D MMM") + " " + prevBEshort,
      prevYearLabel:
        lastWeek.format("dddd") + "ที่แล้ว " + lastWeek.format("D MMM") + " " + lastWeekBEshort,
    };
  }

  // งวดปัจจุบันเทียบกับ "ช่วงเวลาเท่ากัน" ของงวดก่อน — ป้ายต้องบอกว่าเป็นช่วงบางส่วน
  const partial = isPartialPeriod(viewType, { month, year, day }, now);

  if (viewType === "year") {
    const yearNum = parseInt(year, 10);
    const prevYearBE = yearNum - 1 + 543;
    const suffix = partial ? " (ถึง " + now.clone().locale("th").format("D MMM") + ")" : "";
    return {
      prevPeriodLabel: "ปี " + prevYearBE + suffix,
      prevYearLabel: "ปี " + prevYearBE + suffix,
    };
  }

  // default: "month"
  const prevMonth = moment(month).subtract(1, "month");
  const sameMonthLastYear = moment(month).subtract(1, "year");
  const prevBEshort = (prevMonth.year() + 543) % 100;
  const lastYearBEshort = (sameMonthLastYear.year() + 543) % 100;
  // Clamp the "so-far" day to each comparison month's own length — e.g. on
  // 30 March, the previous month (February) never has a 30th, and (rarely) the
  // same month last year can be a shorter/longer February across a leap boundary.
  const prevMonthSuffix = partial
    ? " (1–" + Math.min(now.date(), prevMonth.daysInMonth()) + ")"
    : "";
  const prevYearSuffix = partial
    ? " (1–" + Math.min(now.date(), sameMonthLastYear.daysInMonth()) + ")"
    : "";
  return {
    prevPeriodLabel:
      "เดือน " + prevMonth.locale("th").format("MMM") + " " + prevBEshort + prevMonthSuffix,
    prevYearLabel:
      "เดือน " + sameMonthLastYear.locale("th").format("MMM") + " " + lastYearBEshort + prevYearSuffix,
  };
}

// ---- Range shape ----

/**
 * True when [start, end] spans exactly one calendar month. Accepts both the
 * inclusive end the UI sends (endOf("month") = 23:59:59.999) and the exclusive
 * end the API returns for a completed prevPeriod (next month's midnight), hence
 * the one-second tolerance.
 */
export function isFullMonthRange(start, end) {
  if (!end || start.date() !== 1) return false;
  return Math.abs(end.diff(start.clone().endOf("month"))) < 1000;
}

/** True when [start, end] spans exactly one calendar year. Same tolerance as above. */
export function isFullYearRange(start, end) {
  if (!end || start.month() !== 0 || start.date() !== 1) return false;
  return Math.abs(end.diff(start.clone().endOf("year"))) < 1000;
}

/**
 * True when [start, end] spans exactly one calendar day. Accepts both the
 * inclusive end the UI sends (endOf("day") = 23:59:59.999) and the exclusive
 * end the API returns for a completed prevPeriod (next day's midnight), hence
 * the one-second tolerance. Mirrors isFullMonthRange/isFullYearRange.
 */
export function isFullDayRange(start, end) {
  if (!end || start.hour() !== 0 || start.minute() !== 0 || start.second() !== 0) return false;
  return Math.abs(end.diff(start.clone().endOf("day"))) < 1000;
}

/** True when the selected period has not finished yet — its end is in the future. */
export function isPartialPeriod(viewType, { month, year, day } = {}, now = moment()) {
  return moment(rangeFor(viewType, { month, year, day }).endDt).isAfter(now);
}

/**
 * Thai Buddhist-Era label for a window. The window's own shape decides the form —
 * a partial month must NOT render as a month name, or "1–9 มิ.ย." reads as all of June.
 *
 * Ambiguity: a January-anchored partial window (Jan 1 → Jan 9) has the exact same
 * shape whether it's a partial month-view or a partial year-view (YTD), so shape
 * alone cannot disambiguate it. `viewType` breaks the tie for that one case only —
 * it does not affect isFullYearRange/isFullMonthRange (still shape-only) and does
 * not affect any January window that already extends past January (that can only
 * be YTD, regardless of viewType).
 *
 * `viewType` is OPTIONAL: pass it when labelling a known view (e.g. the dashboard
 * header, which knows whether the user is looking at "month" or "year"). Omit it
 * when labelling a comparison window (e.g. prevPeriod) whose own shape is the only
 * signal available — that is the design's intent and must be preserved.
 *
 * @param {string} startDt ISO
 * @param {string} [endDt] ISO
 * @param {"month"|"year"|"day"} [viewType] Disambiguates a January-anchored
 *   partial window between a partial month and YTD ("year" only). Optional —
 *   shape alone still decides full-year, full-month, full-day/same-day, and
 *   any non-January-anchored range (so a "day" viewType needs no special case
 *   here: isFullDayRange / start.isSame(end, "day") already cover it).
 */
export function formatRange(startDt, endDt, viewType) {
  if (!startDt) return "";
  const start = moment.utc(startDt).local();
  const end = endDt ? moment.utc(endDt).local() : null;
  if (!end) return start.locale("th").format("MMMM") + " " + (start.year() + 543);

  const janAnchored =
    start.month() === 0 && start.date() === 1 && start.year() === end.year();

  // YTD ของปีเดียวกัน → "ปี 2569 (ถึง 9 ก.ค.)"
  // Must precede the full-month/full-day checks below: a year view clamped
  // inside January (e.g. Jan 1 → Jan 31 23:59:59, or Jan 1 → Jan 1 10:00) has
  // the exact same shape as a full/partial January month, so viewType alone —
  // not shape — has to settle it here, before shape gets a vote.
  if (viewType === "year" && janAnchored && !isFullYearRange(start, end)) {
    return "ปี " + (start.year() + 543) + " (ถึง " + end.locale("th").format("D MMM") + ")";
  }

  if (isFullYearRange(start, end)) return "ปี " + (start.year() + 543);
  if (isFullMonthRange(start, end))
    return start.locale("th").format("MMMM") + " " + (start.year() + 543);

  // Single date: either the backend's exclusive next-midnight end for a
  // completed day (I1), or a same-day window that isn't the whole day, i.e.
  // "today so far" (M1).
  if (isFullDayRange(start, end) || start.isSame(end, "day")) {
    return start.locale("th").format("D MMM") + " " + (start.year() + 543);
  }

  // YTD of a comparison window that carries no viewType — fires when the
  // window is January-anchored and extends beyond January, since a range past
  // January can never be a single month, so it must be YTD regardless of
  // viewType.
  if (janAnchored && end.month() > 0) {
    return "ปี " + (start.year() + 543) + " (ถึง " + end.locale("th").format("D MMM") + ")";
  }

  return (
    start.locale("th").format("D MMM") +
    " – " +
    end.locale("th").format("D MMM") +
    " " +
    (end.year() + 543)
  );
}
